import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Bot, Truck, Package, Users, TrendingUp, MessageSquare,
  Zap, Target, Send, ArrowRight, BarChart3, CheckCircle
} from 'lucide-react';
import AIDispatcherChat from '@/components/AIDispatcherChat';

const API = process.env.REACT_APP_BACKEND_URL;

const AIDispatcherPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/api/ai-dispatcher/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-900 via-zinc-900 to-zinc-950 py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-center lg:text-left">
              <Badge className="bg-blue-600 text-white mb-4">
                <Zap className="w-3 h-3 mr-1" /> AI-Powered
              </Badge>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                Meet Your AI Dispatcher
              </h1>
              <p className="text-xl text-zinc-300 mb-6">
                Intelligent load matching, rate negotiation, and 24/7 dispatch automation 
                powered by GPT-5.2
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <Link to="/loads">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                    <Package className="w-5 h-5 mr-2" /> Find Loads
                  </Button>
                </Link>
                <Link to="/driver-signup">
                  <Button size="lg" variant="outline" className="border-zinc-600 hover:bg-zinc-800">
                    <Truck className="w-5 h-5 mr-2" /> Join as Driver
                  </Button>
                </Link>
              </div>
            </div>
            <div className="flex-1 w-full max-w-lg">
              <AIDispatcherChat userType="driver" />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 text-center">
              <MessageSquare className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-3xl font-bold">{stats?.total_conversations || 0}</p>
              <p className="text-zinc-400 text-sm">Conversations</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 text-center">
              <Target className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-3xl font-bold">{stats?.total_matches || 0}</p>
              <p className="text-zinc-400 text-sm">Matches Made</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 text-center">
              <Send className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <p className="text-3xl font-bold">{stats?.total_dispatches || 0}</p>
              <p className="text-zinc-400 text-sm">Dispatches Sent</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 text-center">
              <TrendingUp className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-3xl font-bold">{stats?.negotiation_success_rate || 0}%</p>
              <p className="text-zinc-400 text-sm">Negotiation Success</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Features Section */}
      <div className="container mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-center mb-12">How AI Dispatcher Works</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-blue-500" />
              </div>
              <CardTitle>Auto-Match</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-400">
                AI analyzes 733,000+ drivers to find the perfect match for every load based on 
                equipment, location, and availability.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Equipment matching
                </li>
                <li className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Location proximity
                </li>
                <li className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Instant scoring
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <div className="w-12 h-12 bg-green-600/20 rounded-lg flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-green-500" />
              </div>
              <CardTitle>Smart Chat</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-400">
                24/7 AI assistant answers questions, provides rate estimates, and helps 
                drivers and brokers find what they need.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Natural language
                </li>
                <li className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Industry expertise
                </li>
                <li className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Rate insights
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-purple-500" />
              </div>
              <CardTitle>Auto-Dispatch</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-400">
                Automatically sends load offers to matched drivers via email. 
                No manual outreach needed.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Bulk dispatch
                </li>
                <li className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Personalized emails
                </li>
                <li className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Track responses
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Automate Your Dispatch?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Join 800,000+ trucking professionals on Tempus Dispatch
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/post-load">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-zinc-100">
                Post a Load <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/driver-signup">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Sign Up as Driver
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-zinc-900 py-8 border-t border-zinc-800">
        <div className="container mx-auto px-4 text-center text-zinc-400 text-sm">
          <p>Tempus Dispatch LLC | AI Dispatcher powered by GPT-5.2</p>
          <p className="mt-2">
            <Link to="/" className="text-blue-400 hover:underline">Home</Link>
            {' | '}
            <Link to="/loads" className="text-blue-400 hover:underline">Load Board</Link>
            {' | '}
            <Link to="/admin" className="text-blue-400 hover:underline">Admin</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIDispatcherPage;
